import { z } from 'zod'
import { IMPORT_VARIANTS } from './import-names'
import { MARKER_COLORS, MAX_DISPLAY_NAME, MAX_MARKER_LABEL } from './constants'
import { SESSION_ROLES, TEAM_ROLES } from './roles'
import { recordingStateSchema } from './room'

const id = z.string().min(1).max(64)
/** Server epoch ms. Browsers measure with sub-millisecond precision; the database keeps whole ms. */
export const epochMs = z.number().finite().nonnegative().transform((n) => Math.round(n))
const displayName = z
  .string()
  .trim()
  .min(1, 'Please enter a name')
  .max(MAX_DISPLAY_NAME)
  // control characters and bidi overrides have no business in a name shown to others
  .regex(/^[^\p{Cc}‪-‮⁦-⁩]+$/u, 'Name contains invalid characters')

// ---------- auth ----------

const newPassword = z.string().min(10, 'Use at least 10 characters').max(200)

export const signupBody = z.object({
  email: z.email().max(254).transform((v) => v.toLowerCase()),
  name: displayName,
  password: newPassword,
  /** Token of a team invite: the new account joins that team instead of getting its own. */
  invite: z.string().min(10).max(100).optional(),
  /** Sign-up code for invite-only servers, for people without an invite. */
  code: z.string().max(200).optional(),
})
export type SignupBody = z.input<typeof signupBody>

export const authConfig = z.object({ signup: z.enum(['open', 'invite']) })
export type AuthConfig = z.infer<typeof authConfig>

export const loginBody = z.object({
  email: z.email().max(254).transform((v) => v.toLowerCase()),
  password: z.string().min(1).max(200),
})
export type LoginBody = z.input<typeof loginBody>

export const changePasswordBody = z.object({
  currentPassword: z.string().min(1).max(200),
  newPassword,
})
export type ChangePasswordBody = z.input<typeof changePasswordBody>

/** Created by a team owner for a member; the URL is returned only this once. */
export const passwordResetLink = z.object({ url: z.string(), expiresAt: z.string() })
export type PasswordResetLink = z.infer<typeof passwordResetLink>

export const passwordResetPreview = z.object({ name: z.string(), email: z.string(), expired: z.boolean() })
export type PasswordResetPreview = z.infer<typeof passwordResetPreview>

export const resetPasswordBody = z.object({ password: newPassword })

export const teamSummary = z.object({ id, name: z.string(), role: z.enum(TEAM_ROLES) })
export type TeamSummary = z.infer<typeof teamSummary>

export const meResponse = z.object({
  user: z.object({ id, email: z.string(), name: z.string() }),
  teams: z.array(teamSummary),
})
export type MeResponse = z.infer<typeof meResponse>

// ---------- teams ----------

export const createTeamBody = z.object({ name: z.string().trim().min(1).max(80) })
export const renameTeamBody = createTeamBody

export const teamMember = z.object({
  userId: id,
  name: z.string(),
  email: z.string(),
  role: z.enum(TEAM_ROLES),
  joinedAt: z.string(),
})
export type TeamMember = z.infer<typeof teamMember>

export const updateMemberBody = z.object({ role: z.enum(TEAM_ROLES) })

export const createInviteBody = z.object({ role: z.enum(TEAM_ROLES) })
export const teamInvite = z.object({
  id,
  role: z.enum(TEAM_ROLES),
  /** Only returned once, right after creation. */
  url: z.string().optional(),
  createdAt: z.string(),
  expiresAt: z.string(),
  createdByName: z.string(),
})
export type TeamInvite = z.infer<typeof teamInvite>

export const invitePreview = z.object({
  teamName: z.string(),
  role: z.enum(TEAM_ROLES),
  invitedBy: z.string(),
  expired: z.boolean(),
})
export type InvitePreview = z.infer<typeof invitePreview>

// ---------- studios ----------

export const VIDEO_QUALITIES = ['720p', '1080p'] as const
export type VideoQuality = (typeof VIDEO_QUALITIES)[number]

export const createStudioBody = z.object({ teamId: id, name: z.string().trim().min(1).max(80) })
export const updateStudioBody = z.object({
  name: z.string().trim().min(1).max(80).optional(),
  backupRecording: z.boolean().optional(),
  videoQuality: z.enum(VIDEO_QUALITIES).optional(),
})
export type UpdateStudioBody = z.infer<typeof updateStudioBody>

export const studio = z.object({
  id,
  teamId: id,
  name: z.string(),
  backupRecording: z.boolean(),
  videoQuality: z.enum(VIDEO_QUALITIES),
  createdAt: z.string(),
  /** Producers and owners only. */
  guestUrl: z.string().nullable(),
  live: z.object({ participants: z.number(), recording: z.boolean() }),
  recordingCount: z.number(),
})
export type Studio = z.infer<typeof studio>

// ---------- joining a session ----------

export const joinPreview = z.object({
  studioName: z.string(),
  teamName: z.string(),
  recording: z.boolean(),
  /** The lobby opens the camera at this size, so the preview matches what gets recorded. */
  videoQuality: z.enum(VIDEO_QUALITIES),
})
export type JoinPreview = z.infer<typeof joinPreview>

export const guestJoinBody = z.object({
  name: displayName,
  /** Participant token from an earlier visit; keeps the same identity after a crash or reload. */
  resumeToken: z.string().max(2000).optional(),
})
export type GuestJoinBody = z.input<typeof guestJoinBody>

export const memberJoinBody = z.object({ name: displayName.optional() })

export const joinResponse = z.object({
  participant: z.object({ id, name: z.string(), role: z.enum(SESSION_ROLES) }),
  participantToken: z.string(),
  livekit: z.object({ url: z.string(), token: z.string() }),
  studio: z.object({ id, name: z.string(), teamId: id, videoQuality: z.enum(VIDEO_QUALITIES) }),
  recording: recordingStateSchema.nullable(),
})
export type JoinResponse = z.infer<typeof joinResponse>

export const studioStateResponse = z.object({
  recording: recordingStateSchema.nullable(),
  role: z.enum(SESSION_ROLES),
  serverTime: z.number(),
})
export type StudioStateResponse = z.infer<typeof studioStateResponse>

export const participantActionBody = z.object({
  action: z.enum(['mute-audio', 'mute-video', 'remove', 'make-producer', 'make-guest']),
})
export type ParticipantAction = z.infer<typeof participantActionBody>['action']

// ---------- tracks & uploads ----------

export const TRACK_KINDS = ['camera', 'screen', 'mic'] as const
export type TrackKind = (typeof TRACK_KINDS)[number]

export const registerTrackBody = z.object({
  id: z.uuid(),
  takeId: id,
  kind: z.enum(TRACK_KINDS),
  /** `camera`, `mic`, `screen-1`, `screen-2` … — tracks with the same key form one source. */
  sourceKey: z.string().regex(/^(camera|mic|screen-[1-9])$/),
  mimeType: z.string().max(100),
  /** Server epoch ms of the first recorded sample. */
  startedAt: epochMs,
  width: z.number().int().positive().max(8192).optional(),
  height: z.number().int().positive().max(8192).optional(),
  sampleRate: z.number().int().positive().max(192000).optional(),
  channels: z.number().int().positive().max(8).optional(),
})
export type RegisterTrackBody = z.infer<typeof registerTrackBody>

export const finalizeTrackBody = z.object({
  chunkCount: z.number().int().nonnegative(),
  endedAt: epochMs,
  /** Total PCM frames for mic tracks. */
  frames: z.number().int().nonnegative().optional(),
})
export type FinalizeTrackBody = z.infer<typeof finalizeTrackBody>

/** Headers sent with each chunk PUT. */
export const CHUNK_HEADERS = {
  sha256: 'x-chunk-sha256',
  /** Server epoch ms at which the chunk's first sample was captured (approximate for video). */
  at: 'x-chunk-at',
  /** First PCM frame index of the chunk (mic tracks only). */
  frame: 'x-chunk-frame',
} as const

// ---------- session events, markers, chat ----------

export const CLIENT_EVENT_TYPES = ['connection_lost', 'connection_restored', 'left', 'recording_error', 'storage_low'] as const
export const SERVER_EVENT_TYPES = [
  'participant_joined',
  'participant_left',
  'participant_dropped',
  'recording_started',
  'recording_paused',
  'recording_resumed',
  'recording_stopped',
  'screen_share_started',
  'screen_share_stopped',
  'participant_removed',
  'role_changed',
] as const
export type SessionEventType = (typeof CLIENT_EVENT_TYPES)[number] | (typeof SERVER_EVENT_TYPES)[number]

export const clientEventBody = z.object({
  type: z.enum(CLIENT_EVENT_TYPES),
  at: epochMs,
  /** Duration of an outage, for connection_restored. */
  durationMs: z.number().nonnegative().max(86_400_000).optional(),
  message: z.string().max(300).optional(),
})
export type ClientEventBody = z.infer<typeof clientEventBody>

export const sessionEvent = z.object({
  id,
  at: z.number(),
  type: z.string(),
  participantId: z.string().nullable(),
  participantName: z.string().nullable(),
  data: z.record(z.string(), z.unknown()),
})
export type SessionEvent = z.infer<typeof sessionEvent>

export const createMarkerBody = z.object({
  label: z.string().trim().max(MAX_MARKER_LABEL).default(''),
  color: z.enum(MARKER_COLORS).default('amber'),
  /** Server epoch ms; defaults to now. Clamped to the current take. */
  at: epochMs.optional(),
})
export type CreateMarkerBody = z.input<typeof createMarkerBody>

export const marker = z.object({
  id,
  recordingId: id,
  takeId: z.string().nullable(),
  at: z.number(),
  label: z.string(),
  color: z.enum(MARKER_COLORS),
  authorName: z.string(),
  createdAt: z.string(),
})
export type Marker = z.infer<typeof marker>

/** A marker of the recording that is running, with its place in the recorded material. */
export const sessionMarker = marker.extend({ offsetMs: z.number() })
export type SessionMarker = z.infer<typeof sessionMarker>

// ---------- recordings & media ----------

export const SOURCE_STATUSES = ['waiting', 'processing', 'ready', 'partial', 'failed'] as const
export type SourceStatus = (typeof SOURCE_STATUSES)[number]

export const take = z.object({
  id,
  index: z.number().int(),
  startedAt: z.number(),
  endedAt: z.number().nullable(),
  durationMs: z.number(),
})
export type Take = z.infer<typeof take>

/** One continuous lane in the editor: a participant's camera, mic or one of their screens. */
export const source = z.object({
  /** Stable across takes: `${participantId}:${sourceKey}`. */
  id: z.string(),
  participantId: id,
  participantName: z.string(),
  kind: z.enum(TRACK_KINDS),
  sourceKey: z.string(),
  label: z.string(),
})
export type Source = z.infer<typeof source>

/** Processed media of one source within one take, aligned to the take start. */
export const sourceMedia = z.object({
  id,
  sourceId: z.string(),
  takeId: id,
  status: z.enum(SOURCE_STATUSES),
  durationMs: z.number().nullable(),
  width: z.number().nullable(),
  height: z.number().nullable(),
  hasAudio: z.boolean(),
  /** Ranges (ms from take start) where the source actually has content. */
  availability: z.array(z.tuple([z.number(), z.number()])),
  error: z.string().nullable(),
  urls: z.object({
    master: z.string().nullable(),
    proxy: z.string().nullable(),
    audio: z.string().nullable(),
    audioProxy: z.string().nullable(),
    peaks: z.string().nullable(),
  }),
})
export type SourceMedia = z.infer<typeof sourceMedia>

/** Waveform overview stored next to each mic recording: `rate` peaks per second, each 0..100. */
export interface PeaksFile {
  rate: number
  peaks: number[]
}

export const uploadProgress = z.object({
  participantId: id,
  participantName: z.string(),
  takeId: id,
  sourceKey: z.string(),
  tracks: z.number(),
  finalizedTracks: z.number(),
  receivedChunks: z.number(),
  expectedChunks: z.number().nullable(),
  bytes: z.number(),
  lastChunkAt: z.string().nullable(),
})
export type UploadProgress = z.infer<typeof uploadProgress>

export const RECORDING_STATUSES = ['recording', 'paused', 'ended'] as const

export const recordingSummary = z.object({
  id,
  studioId: id,
  studioName: z.string(),
  title: z.string(),
  status: z.enum(RECORDING_STATUSES),
  startedAt: z.string(),
  endedAt: z.string().nullable(),
  durationMs: z.number(),
  takeCount: z.number(),
  participantNames: z.array(z.string()),
  /** ready | processing | waiting — the worst state over all sources. */
  mediaStatus: z.enum(['none', 'waiting', 'processing', 'ready', 'partial', 'failed']),
})
export type RecordingSummary = z.infer<typeof recordingSummary>

export const IMPORT_MAX_FILES = 64
export const IMPORT_MAX_FILE_BYTES = 50 * 1024 ** 3
export const partSizeFor = (bytes: number) => Math.max(16 * 1024 ** 2, Math.ceil(bytes / 9000))
export const importProbe = z.object({ durationMs: z.number(), video: z.object({ w: z.number(), h: z.number(), fps: z.number() }).nullable(), audio: z.object({ rate: z.number(), channels: z.number() }).nullable() })
export const importAlignment = z.object({ method: z.enum(['session', 'correlation', 'blackdetect', 'manual', 'fallback-aligned']), offsetMs: z.number(), score: z.number(), confidence: z.number(), driftPpm: z.number(), needsReview: z.boolean() })
export const importFile = z.object({
  id, importId: id, name: z.string(), bytes: z.number(), contentType: z.string(),
  status: z.enum(['pending', 'uploading', 'uploaded', 'failed']), variant: z.enum(IMPORT_VARIANTS),
  mapping: z.enum(['auto', 'manual']),
  role: z.enum(['source', 'reference', 'original']), participantId: id.nullable(), participantName: z.string().nullable(),
  sourceKey: z.string().nullable(), referenceFileId: id.nullable(), offsetMs: z.number().nullable(),
  probe: importProbe.nullable(), alignment: importAlignment.nullable(), error: z.string().nullable(),
})
export type ImportFile = z.infer<typeof importFile>
export const importDetail = z.object({ id, recordingId: id, provider: z.enum(['riverside', 'other']), status: z.enum(['draft', 'committed']), error: z.string().nullable(), createdAt: z.string(), committedAt: z.string().nullable(), files: z.array(importFile) })
export type ImportDetail = z.infer<typeof importDetail>
const importFileBody = z.object({ name: z.string().trim().min(1).max(255), bytes: z.number().int().positive().max(IMPORT_MAX_FILE_BYTES), contentType: z.string().min(1).max(100) })
export const addImportFilesBody = z.object({ files: z.array(importFileBody).min(1).max(IMPORT_MAX_FILES) })
export const createImportBody = addImportFilesBody.extend({ files: z.array(importFileBody).max(IMPORT_MAX_FILES), title: z.string().trim().min(1).max(120), provider: z.enum(['riverside', 'other']).default('riverside') })
export const importUrlBody = z.object({ url: z.url().max(4000), name: z.string().trim().min(1).max(255).optional() })
export const updateImportFileBody = z.object({ participantName: displayName.nullable().optional(), role: z.enum(['source', 'reference', 'original']).optional(), sourceKey: z.string().regex(/^(camera|mic|mix|screen-[1-9][0-9]?)$/).nullable().optional(), referenceFileId: id.nullable().optional(), offsetMs: z.number().finite().min(-86400000).max(86400000).nullable().optional() })
export const originalFile = z.object({ id, name: z.string(), participantName: z.string().nullable(), sourceKey: z.string().nullable(), take: z.number().nullable(), offsetMs: z.number().nullable(), alignment: importAlignment.nullable(), bytes: z.number(), downloadUrl: z.string() })
export type OriginalFile = z.infer<typeof originalFile>

export const recordingDetail = recordingSummary.extend({
  origin: z.enum(['studio', 'import']),
  import: importDetail.nullable(),
  teamId: id,
  takes: z.array(take),
  sources: z.array(source),
  media: z.array(sourceMedia),
  uploads: z.array(uploadProgress),
  markers: z.array(marker),
  events: z.array(sessionEvent),
  projectId: z.string().nullable(),
  transferBusyReason: z.enum(['live', 'rendering', 'processing', 'import_draft', 'uploads_pending', 'transfer_active']).nullable(),
  canEdit: z.boolean(),
})
export type RecordingDetail = z.infer<typeof recordingDetail>

export const BACKUP_STATUSES = ['active', 'complete', 'failed'] as const
export const backupFile = z.object({
  id,
  takeId: id,
  takeIndex: z.number(),
  participantId: id,
  participantName: z.string(),
  status: z.enum(BACKUP_STATUSES),
  error: z.string().nullable(),
  downloadUrl: z.string().nullable(),
})
export type BackupFile = z.infer<typeof backupFile>

export const updateRecordingBody = z.object({ title: z.string().trim().min(1).max(120) })

// ---------- editor & renders ----------

export const ASPECTS = ['16:9', '9:16', '1:1'] as const
export type Aspect = (typeof ASPECTS)[number]
export const RESOLUTIONS = ['720p', '1080p', '2160p'] as const
export type Resolution = (typeof RESOLUTIONS)[number]

export const collabTicket = z.object({ token: z.string(), documentName: z.string(), readOnly: z.boolean() })
export type CollabTicket = z.infer<typeof collabTicket>

export const createRenderBody = z.object({ resolution: z.enum(RESOLUTIONS) })

export const RENDER_STATUSES = ['queued', 'rendering', 'done', 'failed'] as const
export const render = z.object({
  id,
  status: z.enum(RENDER_STATUSES),
  progress: z.number(),
  resolution: z.enum(RESOLUTIONS),
  aspect: z.enum(ASPECTS),
  durationMs: z.number().nullable(),
  bytes: z.number().nullable(),
  error: z.string().nullable(),
  createdAt: z.string(),
  finishedAt: z.string().nullable(),
  createdByName: z.string(),
  downloadUrl: z.string().nullable(),
})
export type Render = z.infer<typeof render>

export const apiError = z.object({ error: z.string(), message: z.string(), reason: z.string().optional(), details: z.unknown().optional() })
export type ApiError = z.infer<typeof apiError>

export function sourceId(participantId: string, sourceKey: string): string {
  return `${participantId}:${sourceKey}`
}

export function sourceKindLabel(sourceKey: string): string {
  if (sourceKey === 'camera') return 'Camera'
  if (sourceKey === 'mic') return 'Mic'
  if (sourceKey === 'mix') return 'Mix'
  return `Screen ${sourceKey.slice('screen-'.length)}`
}

export function sourceLabel(name: string, sourceKey: string): string {
  return `${name} · ${sourceKindLabel(sourceKey)}`
}

/** Camera first, then mic, then the screens in the order they were shared. */
export function compareSourceKeys(a: string, b: string): number {
  const rank = (key: string) => (key === 'camera' ? 0 : key === 'mic' ? 1 : key === 'mix' ? 3 : 2)
  return rank(a) - rank(b) || a.localeCompare(b, undefined, { numeric: true })
}

export function sourceKindFromKey(sourceKey: string): TrackKind {
  if (sourceKey === 'camera') return 'camera'
  if (sourceKey === 'mic' || sourceKey === 'mix') return 'mic'
  return 'screen'
}
